import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormikProps, getIn } from "formik";
import { useFeatureEntitlements } from "@frontegg/react";
import { Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, Dollar02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Chip, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import RemovableField from "components/common/RemovableField";
import { CardPolicy } from "utils/cards/cardFormUtils";

export type LimitType =
  | "transactionLimit"
  | "dailyLimit"
  | "weeklyLimit"
  | "monthlyLimit"
  | "totalLimit";

interface Limit {
  value: LimitType;
  label: string;
  shortLabel: string;
}

interface SpendingLimitFieldsProps {
  formik: FormikProps<CardPolicy>;
  // An EXTERNAL card carries limits it does not own, so admin renders it read-only.
  disabled?: boolean;
}

const fieldError = (
  formik: FormikProps<CardPolicy>,
  field: string,
): string | undefined => {
  const touched = getIn(formik.touched, field);
  const error = getIn(formik.errors, field);
  return touched && typeof error === "string" ? error : undefined;
};

const SpendingLimitFields: React.FC<SpendingLimitFieldsProps> = ({
  formik,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { isEntitled: featureCardTypes } =
    useFeatureEntitlements("feature_card_types");

  const availableLimits: Limit[] = useMemo(
    () => [
      {
        value: "transactionLimit" as const,
        label: t("Transaction Limit"),
        shortLabel: t("LimitTypeShort_transaction"),
      },
      {
        value: "dailyLimit" as const,
        label: t("Daily Limit"),
        shortLabel: t("LimitTypeShort_daily"),
      },
      {
        value: "weeklyLimit" as const,
        label: t("Weekly Limit"),
        shortLabel: t("LimitTypeShort_weekly"),
      },
      {
        value: "monthlyLimit" as const,
        label: t("Monthly Limit"),
        shortLabel: t("LimitTypeShort_monthly"),
      },
      ...(featureCardTypes
        ? [
            {
              value: "totalLimit" as const,
              label: t("Total Card Limit"),
              shortLabel: t("LimitTypeShort_total"),
            },
          ]
        : []),
    ],
    [featureCardTypes, t],
  );

  // Start from limits that already carry a value; a fresh form shows none.
  const [selectedLimits, setSelectedLimits] = useState<LimitType[]>(() =>
    availableLimits
      .filter((limit) => formik.values[limit.value])
      .map((limit) => limit.value),
  );

  // Set on add, consumed by the new input's `inputRef` when it mounts.
  const pendingFocus = useRef<LimitType | null>(null);

  useEffect(() => {
    const withValues = availableLimits
      .filter((limit) => formik.values[limit.value])
      .map((limit) => limit.value);
    setSelectedLimits((prev) => {
      const merged = Array.from(new Set([...prev, ...withValues]));
      // A fresh array every run loops against the DS field's own InputBase effect.
      return merged.length === prev.length ? prev : merged;
    });
  }, [availableLimits, formik.values]);

  // Only resetForm swaps initialValues, so identity marks a different preset.
  const seededFrom = useRef(formik.initialValues);
  useEffect(() => {
    if (seededFrom.current === formik.initialValues) return;
    seededFrom.current = formik.initialValues;
    setSelectedLimits(
      availableLimits
        .filter((limit) => formik.initialValues?.[limit.value])
        .map((limit) => limit.value),
    );
  }, [availableLimits, formik.initialValues]);

  const addLimit = (limitType: LimitType) => {
    setSelectedLimits((prev) =>
      prev.includes(limitType) ? prev : [...prev, limitType],
    );
    pendingFocus.current = limitType;
  };

  const removeLimit = (limitType: LimitType) => {
    setSelectedLimits((prev) => prev.filter((limit) => limit !== limitType));
    formik.setFieldValue(limitType, "");
  };

  const unusedLimits = availableLimits.filter(
    (limit) => !selectedLimits.includes(limit.value),
  );

  // A read-only section with nothing set has to say so, or it renders empty.
  if (disabled && selectedLimits.length === 0) {
    return (
      <Typography variant="body1">
        {t("No spending limits are available")}
      </Typography>
    );
  }

  return (
    <Stack gap="var(--space-4)">
      {selectedLimits.map((limitType) => {
        const label =
          availableLimits.find((limit) => limit.value === limitType)?.label ??
          "";
        return (
          <RemovableField
            key={limitType}
            removeLabel={t("Delete")}
            disabled={disabled}
            onRemove={disabled ? undefined : () => removeLimit(limitType)}
          >
            <TextField
              fullWidth
              type="number"
              name={limitType}
              label={label}
              disabled={disabled}
              leading={<HugeiconsIcon icon={Dollar02Icon} size={14} />}
              // ref, not inputRef: the DS TextField overwrites inputRef with its own.
              ref={(node: HTMLInputElement | null) => {
                if (node && pendingFocus.current === limitType) {
                  pendingFocus.current = null;
                  node.focus();
                }
              }}
              value={formik.values[limitType] || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errorText={fieldError(formik, limitType)}
              inputProps={{
                min: 0,
                // Stop the scroll wheel incrementing the amount.
                onWheel: (event: React.WheelEvent<HTMLInputElement>) =>
                  event.currentTarget.blur(),
              }}
            />
          </RemovableField>
        );
      })}

      {unusedLimits.length > 0 && !disabled && (
        <Inline gap="var(--space-2)">
          {unusedLimits.map((limit) => (
            <Chip
              key={limit.value}
              size="sm"
              tone="default"
              leading={<HugeiconsIcon icon={Add01Icon} size={16} />}
              // The pill must announce which limit it adds; the noun alone is not an action.
              aria-label={t("AddSpendingLimit", { limit: limit.label })}
              // The DS Chip derives clickable and tabIndex from onClick.
              onClick={() => addLimit(limit.value)}
            >
              {limit.shortLabel}
            </Chip>
          ))}
        </Inline>
      )}
    </Stack>
  );
};

export default SpendingLimitFields;
